import { HeroMinimal, BookingCTA } from "../components/sections";
import { SectionHeader, FeatureCard } from "../components/sections";
import {
  Zap,
  Search,
  Filter,
  PenTool,
  Users,
  BarChart3,
  Database,
  Send,
  CheckCircle,
} from "lucide-react";

const aiSections = [
  {
    title: "AI Research",
    description:
      "Automated company and prospect research across web, news, databases, and social signals. Generates structured context for every prospect.",
    capabilities: [
      "Company intelligence gathering",
      "Technographic analysis",
      "Hiring & funding signals",
      "Strategic initiative detection",
      "Competitive landscape mapping",
      "Executive profiling",
    ],
    icon: Search,
  },
  {
    title: "Enrichment Intelligence",
    description:
      "AI-enhanced data cleaning, normalization, and gap-filling across multiple data sources. Resolves conflicts and predicts missing values.",
    capabilities: [
      "Multi-source data fusion",
      "Conflict resolution",
      "Predictive field completion",
      "Anomaly detection",
      "Freshness scoring",
      "Quality prediction",
    ],
    icon: Database,
  },
  {
    title: "Classification & Scoring",
    description:
      "Automatic ICP fit scoring, buyer role detection, seniority classification, and intent classification using behavioral and firmographic signals.",
    capabilities: [
      "ICP fit scoring",
      "Buyer role identification",
      "Seniority & authority mapping",
      "Intent signal classification",
      "Purchase timing prediction",
      "Churn risk for current accounts",
    ],
    icon: Filter,
  },
  {
    title: "Segmentation",
    description:
      "Dynamic micro-segmentation based on behavior, firmographics, technographics, intent signals, and engagement history. Updates automatically.",
    capabilities: [
      "Behavioral segmentation",
      "Firmographic clustering",
      "Technographic grouping",
      "Intent-based cohorts",
      "Engagement tiers",
      "Automatic segment refresh",
    ],
    icon: Users,
  },
  {
    title: "Personalization Engine",
    description:
      "Context-aware content generation using research intelligence, not merge tags. Every message reflects actual prospect context.",
    capabilities: [
      "Research-driven personalization",
      "Dynamic content blocks",
      "Tone & style adaptation",
      "Value prop matching",
      "Objection pre-handling",
      "Social proof selection",
    ],
    icon: PenTool,
  },
  {
    title: "Message Generation",
    description:
      "AI-drafted outreach aligned with your voice, positioning, and strategy. Human-reviewed, continuously improved via performance feedback.",
    capabilities: [
      "Framework-based generation",
      "Voice & style consistency",
      "A/B variant creation",
      "Performance-based learning",
      "Compliance checking",
      "Multi-channel adaptation",
    ],
    icon: Send,
  },
  {
    title: "Qualification & Routing",
    description:
      "Scoring and routing based on fit, intent, engagement signals, and response classification. Only sales-ready prospects reach your team.",
    capabilities: [
      "Multi-dimensional scoring",
      "Response classification",
      "Meeting readiness detection",
      "Automated CRM routing",
      "Sales notifications",
      "Nurture path assignment",
    ],
    icon: CheckCircle,
  },
  {
    title: "Workflow Decisions",
    description:
      "Conditional branching and next-best-action logic in sequences. The system adapts based on prospect behavior in real-time.",
    capabilities: [
      "Behavioral triggers",
      "Channel switching logic",
      "Timing optimization",
      "Content variation",
      "Escalation rules",
      "Loop prevention",
    ],
    icon: Zap,
  },
  {
    title: "Campaign Optimization",
    description:
      "Continuous A/B testing analysis, statistical significance testing, and performance-based adjustments across all variables.",
    capabilities: [
      "Statistical test automation",
      "Variant performance tracking",
      "Auto-pause underperformers",
      "Budget reallocation",
      "Model retraining triggers",
      "Insight generation",
    ],
    icon: BarChart3,
  },
];

const humanAiCollaboration = [
  {
    area: "Strategy",
    human: "✓ Primary",
    ai: "Supportive",
    description:
      "ICP definition, positioning, market selection, resource allocation",
  },
  {
    area: "Messaging Direction",
    human: "✓ Primary",
    ai: "Drafting & variation",
    description: "Value props, differentiation, tone, compliance boundaries",
  },
  {
    area: "Quality Control",
    human: "✓ Primary",
    ai: "Pre-screening",
    description:
      "Final review of AI outputs, brand alignment, strategic approval",
  },
  {
    area: "Sales Conversations",
    human: "✓ Exclusive",
    ai: "Preparation only",
    description: "Relationship building, discovery, negotiation, closing",
  },
  {
    area: "Research",
    human: "Oversight",
    ai: "✓ Primary",
    description: "Company intelligence, prospect context, signal monitoring",
  },
  {
    area: "Data Processing",
    human: "Exception handling",
    ai: "✓ Primary",
    description: "Enrichment, verification, normalization, deduplication",
  },
  {
    area: "Personalization",
    human: "Strategy",
    ai: "✓ Execution",
    description: "Content generation, variant creation, context injection",
  },
  {
    area: "Workflow Execution",
    human: "Governance",
    ai: "✓ Operations",
    description: "Sequence deployment, branching, scheduling, CRM sync",
  },
];

function AIAutomationPage() {
  return (
    <>
      <HeroMinimal
        title="AI Where It Creates Leverage. Automation Where It Removes Friction."
        subtitle="Practical AI applied to the repetitive intelligence-heavy work of outbound. Humans remain responsible for strategy, relationships, and closing."
        ctaText="Book a Strategy Call"
        ctaHref="/book"
        secondaryCtaText="See Data & Prospecting"
        secondaryCtaHref="/technology/data-prospecting"
      />

      {/* AI Capabilities */}
      <section className="section" aria-labelledby="capabilities-title">
        <div className="container">
          <SectionHeader
            id="capabilities-title"
            eyebrow="AI Capabilities"
            title="Where AI Operates in the System"
            description="Each capability is a modular component that can be engaged independently or as part of the complete engine."
            align="center"
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {aiSections.map((section, index) => (
              <FeatureCard
                key={section.title}
                icon={<section.icon size={24} className="text-accent" />}
                title={section.title}
                description={section.description}
                className="animate-slide-up"
                style={{ animationDelay: `${index * 50}ms` }}
              />
            ))}
          </div>

          {/* Expandable details */}
          <div className="max-w-5xl mx-auto mt-16 space-y-6">
            {aiSections.map((section, index) => (
              <details
                key={section.title}
                className="group card p-6 animate-slide-up"
                style={{ animationDelay: `${(index + 9) * 50}ms` }}
              >
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <div className="flex items-center gap-3">
                    <section.icon
                      size={24}
                      className="text-accent"
                      aria-hidden="true"
                    />
                    <h3 className="h3 text-text-primary">{section.title}</h3>
                  </div>
                  <span className="text-text-muted">Capabilities →</span>
                </summary>
                <div className="mt-4 pt-4 border-t border-border grid md:grid-cols-2 gap-3 animate-slide-down">
                  {section.capabilities.map((cap, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2 text-sm text-text-secondary"
                    >
                      <CheckCircle
                        size={14}
                        className="text-accent flex-shrink-0"
                        aria-hidden="true"
                      />
                      {cap}
                    </div>
                  ))}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Human + AI Collaboration */}
      <section
        className="section bg-surface border-y border-border"
        aria-labelledby="collaboration-title"
      >
        <div className="container">
          <SectionHeader
            id="collaboration-title"
            eyebrow="Human + AI"
            title="AI Handles the Repetitive Work. Humans Handle the Relationships."
            description="Clear division of responsibility. AI amplifies; humans decide and connect."
            align="center"
          />
          <div className="responsibility-grid">
            {humanAiCollaboration.map((row) => (
              <article className="card responsibility-card" key={row.area}>
                <h3 className="h3">{row.area}</h3>
                <p className="body text-text-secondary">{row.description}</p>
                <dl>
                  <div>
                    <dt>Human responsibility</dt>
                    <dd>{row.human}</dd>
                  </div>
                  <div>
                    <dt>AI contribution</dt>
                    <dd>{row.ai}</dd>
                  </div>
                </dl>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="section" aria-labelledby="philosophy-title">
        <div className="container">
          <SectionHeader
            id="philosophy-title"
            eyebrow="Philosophy"
            title="Practical AI, Not AI Theater"
            description="We don't use AI as a marketing term. Every AI component has a measurable job: reduce manual hours, increase coverage, improve relevance, or accelerate learning."
            align="center"
          />
          <div className="grid md:grid-cols-3 gap-6 mt-12 max-w-5xl mx-auto">
            {[
              {
                title: "Measurable Impact",
                description:
                  "Every AI component tracks hours saved, coverage increased, or quality improved.",
              },
              {
                title: "Human-in-the-Loop",
                description:
                  "AI proposes, humans approve. Critical decisions never fully automated.",
              },
              {
                title: "Continuous Learning",
                description:
                  "Models retrain on closed-loop outcome data, not just engagement metrics.",
              },
            ].map((item, index) => (
              <div
                key={item.title}
                className="card-elevated p-6 text-center animate-slide-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <h3 className="h3 text-text-primary mb-2">{item.title}</h3>
                <p className="body text-text-secondary">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BookingCTA />
    </>
  );
}

export default AIAutomationPage;
